import React from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Star,
  Zap,
  Target,
  Gift,
  Users,
  TrendingUp,
  MapPin,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

// Points Balance Card
export function BalanceCard({ balance, pending, expiring, daysToExpiry }) {
  return (
    <Card className="balance-card overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-primary-foreground/80 text-sm font-medium mb-1">Available Points</p>
            <h2 className="text-4xl font-display font-bold text-primary-foreground">
              {balance?.toLocaleString() || '0'}
            </h2>
          </div>
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Star className="h-6 w-6 text-primary-foreground" />
          </div>
        </div>
        <div className="flex items-center gap-4 pt-4 border-t border-white/20">
          {pending > 0 && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary-foreground/70" />
              <span className="text-sm text-primary-foreground/80">
                {pending.toLocaleString()} pending
              </span>
            </div>
          )}
          {expiring > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="expiring" className="bg-white/20 text-primary-foreground border-0">
                {expiring.toLocaleString()} expiring in {daysToExpiry} days
              </Badge>
            </div>
          )}
        </div>
        <p className="text-xs text-primary-foreground/60 mt-4">
          Use points for Physiomobile sessions or partner rewards
        </p>
      </CardContent>
    </Card>
  );
}

// Quick Action Button
export function QuickAction({ icon: Icon, label, onClick, variant = 'default' }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-200',
        'hover:scale-[1.02] active:scale-[0.98]',
        variant === 'primary' && 'bg-primary/10 text-primary',
        variant === 'success' && 'bg-success/10 text-success',
        variant === 'default' && 'bg-muted text-foreground'
      )}
    >
      <div
        className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center mb-2',
          variant === 'primary' && 'bg-primary/10',
          variant === 'success' && 'bg-success/10',
          variant === 'default' && 'bg-background'
        )}
      >
        <Icon className="h-6 w-6" />
      </div>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

// Earn Method Card
export function EarnMethodCard({ method, onClick }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    info: 'bg-info/10 text-info',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
  };

  return (
    <Card
      className="card-interactive cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              colorClasses[method.color] || colorClasses.primary
            )}
          >
            <Zap className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate">{method.title}</h3>
            <p className="text-sm text-muted-foreground truncate">{method.description}</p>
          </div>
          <div className="text-right">
            <Badge variant="points" className="text-sm">
              +{method.pointsPerAction} pts
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Streak Card
export function StreakCard({ currentStreak, rewards }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Target className="h-5 w-5 text-primary" />
          Streak Rewards
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-3xl font-display font-bold text-primary">{currentStreak}</span>
          <span className="text-muted-foreground">day streak</span>
        </div>
        <div className="space-y-3">
          {rewards?.slice(0, 3).map((reward, index) => (
            <div key={index} className="flex items-center gap-3">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold',
                  reward.achieved
                    ? 'bg-success text-success-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {reward.days}
              </div>
              <div className="flex-1">
                <Progress
                  value={reward.achieved ? 100 : (reward.progress / reward.days) * 100}
                  className="h-2"
                />
              </div>
              <Badge variant={reward.achieved ? 'success' : 'secondary'}>
                +{reward.bonus} pts
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Challenge Card
export function ChallengeCard({ challenge, onStart }) {
  const progress = (challenge.progress / challenge.target) * 100;

  return (
    <Card className="card-interactive">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="font-semibold text-foreground">{challenge.title}</h3>
            <p className="text-sm text-muted-foreground">{challenge.description}</p>
          </div>
          <Badge variant="premium">
            +{challenge.reward.toLocaleString()} pts
          </Badge>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {challenge.progress} / {challenge.target} completed
            </span>
            <span className="font-medium text-primary">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-muted-foreground">
            Ends: {new Date(challenge.endDate).toLocaleDateString()}
          </span>
          <Button size="sm" variant="soft" onClick={onStart}>
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Partner Offer Card
export function PartnerOfferCard({ offer, onClick }) {
  return (
    <Card className="card-interactive cursor-pointer overflow-hidden" onClick={onClick}>
      <CardContent className="p-0">
        <div className="flex items-stretch">
          <div className="w-24 h-24 bg-muted flex items-center justify-center flex-shrink-0">
            <Gift className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="flex-1 p-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-muted-foreground">{offer.partnerName}</span>
                {offer.featured && <Badge variant="premium" className="text-[10px] px-1.5">Featured</Badge>}
              </div>
              <h3 className="font-semibold text-foreground text-sm leading-tight">{offer.title}</h3>
            </div>
            <div className="flex items-center justify-between mt-2">
              <Badge variant="points">{offer.pointsCost.toLocaleString()} pts</Badge>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {offer.distance}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Internal Redemption Card
export function InternalRedemptionCard({ item, onRedeem }) {
  return (
    <Card className={cn('card-interactive', !item.available && 'opacity-60')}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="font-semibold text-foreground">{item.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
          </div>
          <Badge variant="success">{item.savingsValue}</Badge>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Badge variant="points" className="mr-2">
              {item.pointsCost.toLocaleString()} pts
            </Badge>
            <span className="text-xs text-muted-foreground">Valid {item.validity}</span>
          </div>
          <Button
            size="sm"
            variant={item.available ? 'default' : 'secondary'}
            onClick={() => item.available && onRedeem(item)}
            disabled={!item.available}
          >
            {item.available ? 'Redeem' : 'Unavailable'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Transaction Row
export function TransactionRow({ transaction, onClick }) {
  const isEarn = transaction.type === 'earn';
  const isRedeem = transaction.type === 'redeem';

  return (
    <div className="transaction-row" onClick={onClick}>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            isEarn && 'bg-success/10',
            isRedeem && 'bg-destructive/10',
            !isEarn && !isRedeem && 'bg-muted'
          )}
        >
          {isEarn ? (
            <ArrowUpRight className="h-5 w-5 text-success" />
          ) : isRedeem ? (
            <ArrowDownRight className="h-5 w-5 text-destructive" />
          ) : (
            <Clock className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">{transaction.description}</p>
          <p className="text-xs text-muted-foreground">
            {transaction.date} • {transaction.source}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p
          className={cn(
            'font-semibold',
            isEarn && 'text-success',
            isRedeem && 'text-destructive',
            !isEarn && !isRedeem && 'text-muted-foreground'
          )}
        >
          {isEarn ? '+' : ''}
          {transaction.points.toLocaleString()}
        </p>
        <Badge variant={transaction.status === 'completed' ? 'success' : 'pending'} className="text-[10px]">
          {transaction.status}
        </Badge>
      </div>
    </div>
  );
}

// Voucher Card
export function VoucherCard({ voucher, onClick }) {
  const statusVariants = {
    active: 'active',
    used: 'secondary',
    expired: 'expired',
  };

  const daysUntilExpiry = voucher.status === 'active'
    ? Math.ceil((new Date(voucher.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Card
      className={cn(
        'card-interactive cursor-pointer overflow-hidden',
        voucher.status !== 'active' && 'opacity-60'
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-xs text-muted-foreground mb-1">{voucher.partnerName}</p>
            <h3 className="font-semibold text-foreground">{voucher.title}</h3>
          </div>
          <Badge variant={statusVariants[voucher.status]}>
            {voucher.status.charAt(0).toUpperCase() + voucher.status.slice(1)}
          </Badge>
        </div>
        <div className="bg-muted rounded-lg p-3 mb-3">
          <p className="text-xs text-muted-foreground mb-1">Voucher Code</p>
          <p className="font-mono font-semibold text-foreground">{voucher.voucherCode}</p>
        </div>
        <div className="flex items-center justify-between text-sm">
          {voucher.status === 'active' && daysUntilExpiry !== null && (
            <span className={cn(
              'text-muted-foreground',
              daysUntilExpiry <= 7 && 'text-warning'
            )}>
              {daysUntilExpiry > 0 ? `${daysUntilExpiry} days left` : 'Expires today'}
            </span>
          )}
          {voucher.status === 'used' && (
            <span className="text-muted-foreground">Used on {voucher.usedDate}</span>
          )}
          {voucher.status === 'expired' && (
            <span className="text-muted-foreground">Expired</span>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

// Referral Card
export function ReferralCard({ referralData, onShare }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-5 w-5 text-primary" />
          Refer & Earn
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Invite friends and earn 500 points for each successful signup!
        </p>
        <div className="bg-muted rounded-lg p-4 mb-4">
          <p className="text-xs text-muted-foreground mb-1">Your Referral Code</p>
          <div className="flex items-center justify-between">
            <p className="font-mono font-bold text-lg text-primary">{referralData.code}</p>
            <Button size="sm" variant="soft" onClick={onShare}>
              Share
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-display font-bold text-foreground">
              {referralData.totalInvites}
            </p>
            <p className="text-xs text-muted-foreground">Invited</p>
          </div>
          <div>
            <p className="text-2xl font-display font-bold text-success">
              {referralData.successfulSignups}
            </p>
            <p className="text-xs text-muted-foreground">Signed Up</p>
          </div>
          <div>
            <p className="text-2xl font-display font-bold text-primary">
              {referralData.earnedFromReferrals.toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Earned</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// KPI Card for Admin
export function KPICard({ title, value, change, changeType, icon: Icon, variant = 'default' }) {
  const isPositive = changeType === 'positive';
  const isNegative = changeType === 'negative';

  return (
    <Card className={cn(variant === 'primary' && 'kpi-card-primary')}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className={cn(
              'text-sm font-medium mb-1',
              variant === 'primary' ? 'text-primary-foreground/80' : 'text-muted-foreground'
            )}>
              {title}
            </p>
            <h3 className={cn(
              'text-2xl font-display font-bold',
              variant === 'primary' ? 'text-primary-foreground' : 'text-foreground'
            )}>
              {value}
            </h3>
          </div>
          {Icon && (
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center',
              variant === 'primary' ? 'bg-white/20' : 'bg-muted'
            )}>
              <Icon className={cn(
                'h-5 w-5',
                variant === 'primary' ? 'text-primary-foreground' : 'text-muted-foreground'
              )} />
            </div>
          )}
        </div>
        {change !== undefined && (
          <div className="flex items-center gap-1 mt-3">
            {isPositive ? (
              <TrendingUp className="h-4 w-4 text-success" />
            ) : isNegative ? (
              <TrendingUp className="h-4 w-4 text-destructive rotate-180" />
            ) : null}
            <span className={cn(
              'text-sm font-medium',
              isPositive && 'text-success',
              isNegative && 'text-destructive',
              !isPositive && !isNegative && 'text-muted-foreground'
            )}>
              {isPositive ? '+' : ''}{change}%
            </span>
            <span className={cn(
              'text-sm',
              variant === 'primary' ? 'text-primary-foreground/60' : 'text-muted-foreground'
            )}>
              vs last month
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Stats Mini Card
export function StatsMiniCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-muted/50 rounded-lg p-4">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-background flex items-center justify-center">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-semibold text-foreground">{value}</p>
        </div>
      </div>
    </div>
  );
}
